import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, CreditCard, Truck, MapPin, Store } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false };

export default function Checkout() {
  const { cart, refreshCart } = useShop();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [fulfillment, setFulfillment] = useState("delivery");
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stores, setStores] = useState([]);
  const [pickupStore, setPickupStore] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [delivery, setDelivery] = useState("Standard Delivery");
  const [payment, setPayment] = useState("COD");
  const [placed, setPlaced] = useState(null);

  const isPickup = fulfillment === "pickup";
  const deliveryCharge = isPickup || cart.total >= 999 ? 0 : 49;

  useEffect(() => {
    if (loading) return;
    if (!user?.user_id) { navigate("/login"); return; }
    api.get("/addresses").then((r) => { setAddresses(r.data); if (r.data.length) setSelected(r.data.find((a) => a.is_default)?.id || r.data[0].id); });
    api.get("/stores").then((r) => setStores(r.data));
  }, [user, loading, navigate]);

  const saveAddress = async () => {
    for (const k of ["name", "phone", "line1", "city", "state", "pincode"]) if (!form[k]) { toast.error("Please complete the address"); return; }
    const { data } = await api.post("/addresses", form);
    setAddresses((a) => [...a, data]); setSelected(data.id); setAdding(false); setForm(EMPTY);
  };

  const continueStep1 = () => {
    if (isPickup) { if (!pickupStore) { toast.error("Select a pickup store"); return; } setStep(3); }
    else { if (!selected) { toast.error("Select a delivery address"); return; } setStep(2); }
  };

  const placeOrder = async () => {
    const payload = { payment_method: payment, delivery_method: delivery, fulfillment_type: fulfillment };
    if (isPickup) payload.pickup_store = stores.find((s) => s.slug === pickupStore);
    else payload.address = addresses.find((a) => a.id === selected);
    try {
      const { data } = await api.post("/orders", payload);
      setPlaced(data); refreshCart();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); }
  };

  if (loading) return <div className="py-32 text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gold border-t-transparent" /></div>;

  if (placed) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-mart-home/15"><Check className="h-8 w-8 text-mart-home" /></div>
        <h1 className="mt-6 text-3xl font-extrabold text-midnight">Order Confirmed!</h1>
        <p className="mt-2 text-muted-foreground">Order <b className="text-midnight" data-testid="order-number">{placed.order_number}</b> placed successfully.</p>
        <p className="mt-1 text-sm text-muted-foreground">Total {inr(placed.grand_total)} • {placed.payment_method}</p>
        {placed.fulfillment_type === "pickup" && <p className="mt-1 text-sm text-purple-700">Ready for pickup at {placed.pickup_store?.name}. We'll notify you when it's ready.</p>}
        {placed.installation_status && <p className="mt-1 text-sm text-mart-wood">Schedule your furniture installation anytime from your Orders page.</p>}
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={() => navigate("/account/orders")} className="rounded-full bg-midnight px-6 py-3 font-bold text-white" data-testid="view-orders-btn">View Orders</button>
          <button onClick={() => navigate("/shop")} className="rounded-full border border-border px-6 py-3 font-bold">Continue Shopping</button>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return <div className="py-24 text-center"><p className="text-lg font-bold">Your cart is empty</p><button onClick={() => navigate("/shop")} className="mt-4 rounded-full bg-midnight px-6 py-3 font-bold text-white">Shop</button></div>;
  }

  const steps = [[1, isPickup ? "Pickup" : "Address", isPickup ? Store : MapPin], [2, "Delivery", Truck], [3, "Payment", CreditCard]];

  return (
    <div className="mx-auto max-w-[1100px] px-4 md:px-8 py-10">
      <h1 className="text-3xl font-extrabold text-midnight mb-6">Checkout</h1>
      <div className="mb-8 flex gap-2">
        {steps.map(([n, label, Icon]) => (
          (isPickup && n === 2) ? null : (
            <div key={n} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${step >= n ? "bg-midnight text-white" : "bg-secondary text-muted-foreground"}`}>
              <Icon className="h-4 w-4" /> {label}
            </div>
          )
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {step === 1 && (
            <div className="rounded-3xl border border-border p-6" data-testid="step-address">
              {/* Fulfillment toggle */}
              <div className="mb-5 grid grid-cols-2 gap-3">
                {[["delivery", "Home Delivery", Truck], ["pickup", "Store Pickup", Store]].map(([v, l, Icon]) => (
                  <button key={v} onClick={() => setFulfillment(v)} data-testid={`fulfillment-${v}`}
                    className={`flex items-center gap-2 rounded-2xl border p-4 text-sm font-bold ${fulfillment === v ? "border-gold bg-gold/5 text-midnight" : "border-border text-muted-foreground"}`}>
                    <Icon className="h-5 w-5" /> {l}
                  </button>
                ))}
              </div>

              {isPickup ? (
                <div data-testid="pickup-store-section">
                  <h2 className="text-lg font-extrabold mb-1">Choose a KRVMart Store</h2>
                  <p className="mb-4 text-xs text-muted-foreground">Reserve online and collect at your chosen store. (Click & Collect)</p>
                  <div className="space-y-3">
                    {stores.map((s) => (
                      <label key={s.slug} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer ${pickupStore === s.slug ? "border-gold bg-gold/5" : "border-border"}`}>
                        <input type="radio" checked={pickupStore === s.slug} onChange={() => setPickupStore(s.slug)} data-testid={`pickup-store-${s.slug}`} className="mt-1 accent-gold" />
                        <div className="text-sm"><p className="font-bold text-midnight">{s.name} <span className="ml-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold-dark uppercase">{s.status}</span></p><p className="text-muted-foreground">{s.address}</p></div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-lg font-extrabold mb-4">Delivery Address</h2>
                  <div className="space-y-3">
                    {addresses.map((a) => (
                      <label key={a.id} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer ${selected === a.id ? "border-gold bg-gold/5" : "border-border"}`}>
                        <input type="radio" checked={selected === a.id} onChange={() => setSelected(a.id)} className="mt-1 accent-gold" />
                        <div className="text-sm"><p className="font-bold text-midnight">{a.name} • {a.phone}</p><p className="text-muted-foreground">{a.line1}, {a.line2 && a.line2 + ", "}{a.city}, {a.state} - {a.pincode}</p></div>
                      </label>
                    ))}
                  </div>
                  {adding || addresses.length === 0 ? (
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      {[["name", "Full name"], ["phone", "Phone"], ["line1", "Address line 1"], ["line2", "Address line 2 (optional)"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, ph]) => (
                        <input key={k} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`addr-${k}`} className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
                      ))}
                      <button onClick={saveAddress} data-testid="save-address-btn" className="rounded-full bg-midnight py-3 font-bold text-white sm:col-span-2">Save Address</button>
                    </div>
                  ) : (
                    <button onClick={() => setAdding(true)} className="mt-4 text-sm font-semibold text-gold-dark">+ Add new address</button>
                  )}
                </>
              )}
              <button onClick={continueStep1} data-testid="to-delivery-btn" className="mt-5 rounded-full bg-gold px-8 py-3 font-bold text-midnight">Continue</button>
            </div>
          )}
          {step === 2 && !isPickup && (
            <div className="rounded-3xl border border-border p-6" data-testid="step-delivery">
              <h2 className="text-lg font-extrabold mb-4">Delivery Method</h2>
              {[["Standard Delivery", "2-4 business days", "Free above ₹999"], ["Express Delivery", "1-2 business days", "+₹99"]].map(([m, d, c]) => (
                <label key={m} className={`flex justify-between rounded-2xl border p-4 cursor-pointer mb-3 ${delivery === m ? "border-gold bg-gold/5" : "border-border"}`}>
                  <div className="flex gap-3"><input type="radio" checked={delivery === m} onChange={() => setDelivery(m)} className="accent-gold" /><div><p className="text-sm font-bold text-midnight">{m}</p><p className="text-xs text-muted-foreground">{d}</p></div></div>
                  <span className="text-sm font-semibold">{c}</span>
                </label>
              ))}
              <div className="flex gap-3"><button onClick={() => setStep(1)} className="rounded-full border border-border px-6 py-3 font-bold">Back</button><button onClick={() => setStep(3)} data-testid="to-payment-btn" className="rounded-full bg-gold px-8 py-3 font-bold text-midnight">Continue</button></div>
            </div>
          )}
          {step === 3 && (
            <div className="rounded-3xl border border-border p-6" data-testid="step-payment">
              <h2 className="text-lg font-extrabold mb-2">Payment Method</h2>
              <p className="mb-4 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">Online payments (UPI, Cards, Net Banking, Wallets, EMI) will be enabled once our payment gateway integration goes live. For now, Cash on Delivery / Pay at Store is available.</p>
              {[["COD", isPickup ? "Pay at Store" : "Cash on Delivery", true], ["UPI", "UPI", false], ["CARD", "Credit / Debit Card", false], ["NETBANKING", "Net Banking", false], ["EMI", "EMI", false]].map(([v, l, enabled]) => (
                <label key={v} className={`flex items-center gap-3 rounded-2xl border p-4 mb-2 ${!enabled ? "opacity-50 cursor-not-allowed" : payment === v ? "border-gold bg-gold/5 cursor-pointer" : "border-border cursor-pointer"}`}>
                  <input type="radio" disabled={!enabled} checked={payment === v} onChange={() => enabled && setPayment(v)} className="accent-gold" data-testid={`pay-${v}`} />
                  <span className="text-sm font-bold text-midnight">{l}</span>{!enabled && <span className="ml-auto text-xs text-muted-foreground">Coming soon</span>}
                </label>
              ))}
              <div className="flex gap-3 mt-4"><button onClick={() => setStep(isPickup ? 1 : 2)} className="rounded-full border border-border px-6 py-3 font-bold">Back</button><button onClick={placeOrder} data-testid="place-order-btn" className="rounded-full bg-gold px-8 py-3 font-bold text-midnight">Place Order</button></div>
            </div>
          )}
        </div>

        <div className="h-fit rounded-3xl border border-border p-6">
          <h2 className="text-lg font-extrabold mb-4">Summary</h2>
          <div className="space-y-3 max-h-64 overflow-auto hide-scrollbar mb-4">
            {cart.items.map(({ product, quantity }) => (
              <div key={product.id} className="flex gap-3"><img src={product.images[0]} alt="" className="h-12 w-12 rounded-lg object-cover" /><div className="flex-1 text-sm"><p className="font-semibold text-midnight line-clamp-1">{product.name}</p><p className="text-muted-foreground">Qty {quantity}</p></div><span className="text-sm font-bold">{inr(product.price * quantity)}</span></div>
            ))}
          </div>
          <div className="border-t border-border pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{inr(cart.total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{isPickup ? "Pickup" : "Delivery"}</span><span className="font-semibold">{deliveryCharge === 0 ? "Free" : inr(deliveryCharge)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 text-base"><span className="font-bold">Total</span><span className="font-extrabold">{inr(cart.total + deliveryCharge)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
